import { useState, forwardRef, useImperativeHandle, ForwardedRef, useEffect, useRef } from 'react'
import { Card ,CardHeader, CardBody, Input, FormFeedback, Label} from 'reactstrap';
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { ORG_VALUE_LISTS } from "../../../../graphql/valuelist-gql";
import { IValueListLight } from "../../../../types/valuelist";
import AddressTypeahead from '../../../common/typeahead/address-typeahead';
import { IAddressUsage, IAddressInput, IAddressLight } from '../../../../types/contact';
import Select from '../../../common/select/select';
import Info from '../../../common/info';
import Textarea, { TextareaHandler } from '../../../common/textarea/textarea';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';
import { GET_CONTACT_ADDRESSE_BY_ID } from '../../../../graphql/address-gql';
import { IAddress } from '../../../../types/address';

export type AddressHandler = {
    getAddress: () => IAddressInput;
    validate: (inclMandatoryCheck: boolean) => boolean;
};

type AddressDetailsProps = {
    addressUsage?: IAddressUsage;
    mandatory: boolean;
    onChange: () => void;
    onError?: (error: string) => void;
    autoFocus?: boolean;
}

const AddressDetails = forwardRef<AddressHandler, AddressDetailsProps>((props: AddressDetailsProps,ref:ForwardedRef<any>) => {
    const { addressUsage, mandatory, onChange, onError, autoFocus } = props
    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()
    const [addressInput, setAddressInput] = useState<IAddressInput>()
    const [selectedAddress, setSelectedAddress] = useState<IAddressLight | undefined>(undefined)
    const [addressError, setAddressError] = useState<string>('')
    const [unitNumberError, setUnitNumberError] = useState<string>('')
    const [unitNumberFrenchError, setUnitNumberFrenchError] = useState<string>('')
    const [floorError, setFloorError] = useState<string>('')
    const [floorFrenchError, setFloorFrenchError] = useState<string>('')
    const [addressChanged,setAddressChanged] = useState<boolean>(false)
    const [getAddress, {loading, data}] = useLazyQuery(GET_CONTACT_ADDRESSE_BY_ID);
    const [frenchAddressText1, setFrenchAddressText1] = useState<string>('')

    const addInfoRef = useRef<TextareaHandler>(null)
    const oprInfoRef = useRef<TextareaHandler>(null)

    const reverseStreetType = ['AVE', 'BLVD', 'ST', 'WAY']

    useEffect(() => {
        setAddressInput({
            addressUsageId: addressUsage?.id || 0,
            addressId: addressUsage?.addressId || 0,
            unitDesignatorId: addressUsage?.unitDesignator?.id || 0,
            unitNumber: addressUsage?.unitNumber || '',
            unitNumberFrench: addressUsage?.unitNumberFrench || '',
            unitNumberFrenchSync: addressUsage?.unitNumberFrenchSync || false,
            floorOrLevel: addressUsage?.floorOrLevel || '',
            levelNumberFrench: addressUsage?.levelNumberFrench || '',
            levelNumberFrenchSync: addressUsage?.levelNumberFrenchSync || false,
            additionalInfo: addressUsage?.additionalInfo || '',
            additionalInfoFrench: addressUsage?.additionalInfoFrench || '',
            additionalInfoFrenchSync: addressUsage?.additionalInfoFrenchSync || false,
            openingTimeId: addressUsage?.openingTime?.id || 0,
            closingTimeId: addressUsage?.closingTime?.id || 0,
            operationalInfo: addressUsage?.operationalInfo || '',
            operationalInfoFrench: addressUsage?.operationalInfoFrench || '',
            operationalInfoFrenchSync: addressUsage?.operationalInfoFrenchSync || false,
            officeLanguageId: addressUsage?.officeLanguage?.id || 0
        })

        if(addressUsage?.addressId && frenchState.isFrenchChecked){
            
            getAddress({variables: {id: addressUsage.addressId}, fetchPolicy: 'network-only'}).then(response =>{
                const addressObj: IAddress = response.data?.getContactAddressById
                const streetTypeCodeObj = orgValueLists.streetTypeCodes.find((item: { id: number; }) => {return Number(item.id) === addressObj.streetTypeCodeId} )
                const isReverseStreeType = reverseStreetType.find(item => item === streetTypeCodeObj?.code )
                let frAddress = addressObj.buildingNameFrench ? addressObj.buildingNameFrench +',' : ""
                if(isReverseStreeType){
                    frAddress = addressObj.civicNumber ? frAddress +' ' + addressObj.civicNumber : frAddress
                    frAddress = addressObj.civicNumberSuffix ? frAddress +' ' + addressObj.civicNumberSuffix +",": frAddress
                    frAddress = addressObj.streetTypeFrench ? frAddress +' ' + addressObj.streetTypeFrench: frAddress
                    frAddress = addressObj.streetName ?  frAddress +' ' + addressObj.streetName : frAddress
                }else{
                    frAddress = addressObj.civicNumber ? frAddress +' ' + addressObj.civicNumber : frAddress
                    frAddress = addressObj.civicNumberSuffix ? frAddress +' ' + addressObj.civicNumberSuffix : frAddress
                    frAddress = addressObj.streetName ?  frAddress +' ' + addressObj.streetName : frAddress
                    frAddress = addressObj.streetTypeFrench ? frAddress +' ' + addressObj.streetTypeFrench: frAddress
                }
                
                frAddress = addressObj.streetDirFrench ? frAddress +' ' + addressObj.streetDirFrench  : frAddress
                if(addressObj.poBoxNumber || addressObj.routeServiceNumber || addressObj.siteNumber ||addressObj.compartmentNumber || addressObj.installationName || addressObj.generalDeliveryFrench)
                frAddress = frAddress +","
                frAddress = addressObj.poBoxNumber ? frAddress +' ' + addressObj.deliveryTypeFrench +' '+ addressObj.poBoxNumber  : frAddress
                if(addressObj.routeServiceNumber){
                    frAddress = frAddress + '\n '
                    frAddress = addressObj.routeServiceTypeFrench ? frAddress + addressObj.routeServiceTypeFrench + ' ' + addressObj.routeServiceNumber : frAddress + addressObj.routeServiceNumber
                }
                frAddress = addressObj.siteNumber? frAddress + '\r\nSITE ' + addressObj.siteNumber: frAddress
                frAddress = addressObj.compartmentNumber? frAddress + '\r\nCOMP ' + addressObj.compartmentNumber: frAddress
                frAddress = addressObj.installationName? frAddress + ', \r\n' + addressObj.installationTypeFrench+ ' ' + addressObj.installationName : frAddress
                frAddress = addressObj.generalDeliveryFrench? frAddress + '\r\n ' + addressObj.generalDeliveryFrench : frAddress

               setFrenchAddressText1(frAddress)

            })
        }
    }, [addressUsage])

    const client = useApolloClient()
    const { orgValueLists } =  client.readQuery({query: ORG_VALUE_LISTS})

    useEffect(() => {
        onChange()
    }, [addressInput])

    useImperativeHandle(ref, () => ({
        getAddress() {
            return addressInput
        },
        validate(inclMandatoryCheck: boolean) {
            return validate(inclMandatoryCheck)
        }
    }), [addressInput])

    const validate = (inclMandatoryCheck: boolean) => {
        if(isFrenchTranslator && frenchState.isFrenchChecked) return true
        let valid: boolean = !!addInfoRef.current?.validate() && !!oprInfoRef.current?.validate() && !unitNumberError && !floorError

        if (valid && inclMandatoryCheck) {

            valid = !!addressInput?.addressId
                    || !!addressUsage
                    || !mandatory && !addressInput?.unitDesignatorId && !addressInput?.unitNumber && !addressInput?.floorOrLevel
                        && !addressInput?.additionalInfo && !addressInput?.openingTimeId && !addressInput?.closingTimeId
                        && !addressInput?.operationalInfo && !addressInput?.officeLanguageId
            if (!valid && addressChanged) setAddressError('Address is required.')
            else setAddressError('')

        } else {
            setAddressError('')
        }

        return valid
    }

    const addressSelected = (address: IAddressLight | undefined) => {
        setAddressChanged(true)
        setSelectedAddress(address)
        setAddressInput({...addressInput,  addressId: address?.id})
    }

    const changeUnitDesignator = (event: any) => {
        setAddressInput({...addressInput,  unitDesignatorId: event.target.value})
    }

    const changeUnitNumber = (event:any) => {
        if (event.target.value?.length > 15) {
            setUnitNumberError("This value is too long. It should have 15 characters or fewer.")
        } else  {
            setUnitNumberError('')
        }
        setAddressInput({...addressInput,  unitNumber: event.target.value,unitNumberFrenchSync: false})
    }



    const changeUnitNumberTranslated = (event:any) => {
        setAddressInput({...addressInput,  unitNumberFrenchSync: event.target.checked})
    }

    const changeFloorOrLevel = (event:any) => {
        if (event.target.value?.length > 30) {
            setFloorError("This value is too long. It should have 30 characters or fewer.")
        } else  {
            setFloorError('')
        }
        setAddressInput({...addressInput, floorOrLevel: event.target.value,levelNumberFrenchSync: false})
    }

    const changeFloorOrLevelTranslated = (event:any) => {
        setAddressInput({...addressInput,  levelNumberFrenchSync: event.target.checked})
    }


    const changeAdditionalInfo = (value: string) => {
        setAddressInput({...addressInput,  additionalInfo: value, additionalInfoFrenchSync: false})
    }

    const changeAdditionalInfoTranslated = (event:any) => {
        setAddressInput({...addressInput,  additionalInfoFrenchSync: event.target.checked})
    }


    const changeOpeningTime = (event:any) => {
        setAddressInput({...addressInput,openingTimeId: event.target.value})
    }

    const changeClosingTime = (event:any) => {
        setAddressInput({...addressInput, closingTimeId: event.target.value})
    }

    const changeOperationalInfo = (value: string) => {
        setAddressInput({...addressInput, operationalInfo: value, operationalInfoFrenchSync: false})
    }

    const changeOperationalInfoTranslated = (event:any) => {
        setAddressInput({...addressInput,  operationalInfoFrenchSync: event.target.checked})
    }


    const changeLanguageProvided = (event:any) => {
        setAddressInput({...addressInput, officeLanguageId: event.target.value})
    }

    const addressValidate = (error:string) => {
        setAddressError(error)
        onError && onError(error)
    }

    const onAddressBlur = () => {
        //!selectedAddress && setAddressError("Address is required.")
    }

    const onInput = (str:string) => {
        str && setAddressError('')
    }

    return (
        <>
            <Card style={{ marginBottom: '1rem' }} >
                <CardHeader> Address Information </CardHeader>

                <CardBody>
                    {!!addressUsage &&
                        <div className='row' style={{ backgroundColor: 'lightgrey'}}>
                            <div className='col-md-6'>
                                <div style={{padding: '10px'}}>
                                    <div style={{fontWeight: 'bold', marginBottom: '5px'}}>Current Address:</div>
                                    <div>{addressUsage?.addressTxt || ''}<br/>{addressUsage?.addressTxt2 || ''}</div>
                                </div>
                            </div>
                            { frenchState.isFrenchChecked && <div className='col-md-6'>
                                <div style={{padding: '10px'}}>
                                    <div style={{fontWeight: 'bold', marginBottom: '5px'}}>Current Address in French:</div>
                                    <div lang="fr">{frenchAddressText1}<br/>{addressUsage?.addressTxt2 || ''}</div>
                                </div>
                            </div>
                           }
                        </div>
                    }
                  { !isFrenchTranslator && <div className='row'>
                        {!!addressUsage ?
                            <div className='col-md-3'><label htmlFor="selectAddress">New Address:</label></div>
                            :
                            <div className='col-md-3'><label htmlFor="selectAddress">Address: {mandatory ? '(required)' : ''}</label></div>
                        }
                        <div className='col-md-9'>
                            <AddressTypeahead 
                                id="selectAddress" 
                                addressSelected={addressSelected} 
                                error={addressError} 
                                validate={addressValidate} 
                                onBlur={onAddressBlur} 
                                onInput={onInput} 
                                includeInactive={false}
                                autoFocus={autoFocus}
                                />
                        </div>
                    </div>
                   }
                    {
                        !isFrenchTranslator && <div className='row'>
                            <div className='col-md-12'>
                                {!!addressUsage && <Info color="warning">Selecting a new address will replace the current address.</Info>}
                                {selectedAddress &&
                                    <div style={{ padding: '10px', backgroundColor: 'lightgrey' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>New Address:</div>
                                        <div>{selectedAddress?.addressTxt || ''}<br />{selectedAddress?.addressTxt2 || ''}</div>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="unitDesinator">Unit Designator:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Select id="unitDesinator" value={addressInput?.unitDesignatorId || ''} onChange={changeUnitDesignator} style={{ width: '100%' }}>
                                <option value="" disabled selected hidden>Select a unit designator</option>
                                <option value=''></option>
                                {orgValueLists.orgUnitDesignators.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                            </Select>
                        </div>
                        }
                        {isFrenchTranslator &&
                            <div className='col-md-9'>
                                {orgValueLists.orgUnitDesignators.map((item: IValueListLight) => {
                                    if (item.id === addressInput?.unitDesignatorId) {
                                        return item.label;
                                    }
                                    return '';
                                })}
                            </div>
                        }
                    </div>
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="unitNumber">Unit Number: </label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Input id="unitNumber"
                                type="text"
                                style={{ width: '100%' }}
                                value={addressInput?.unitNumber || ''}
                                onChange={changeUnitNumber}
                                placeholder='Enter a unit number'
                                invalid={!!unitNumberError}
                            />
                            <FormFeedback>{unitNumberError}</FormFeedback>
                        </div>
                        }
                        {isFrenchTranslator && <div className='col-md-9'>
                            {addressInput?.unitNumber || ''}
                        </div>}
                    </div>

                    {
                        frenchState.isFrenchChecked &&
                        <>
                            <div className='row'>
                                <div className='col-md-3'><label htmlFor="frunitNumber">Unit Number (French): </label></div>
                                {<div className='col-md-9' lang="fr">
                                    {addressInput?.unitNumberFrench || ''}
                                </div>
                                }
                            </div>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <Input id="unitNumberFrenchSync" type="checkbox" checked={addressInput?.unitNumberFrenchSync} onChange={changeUnitNumberTranslated}  disabled = {!addressInput?.unitNumberFrench}/> {' '}
                                    <Label for="unitNumberFrenchSync" role="region" aria-label='Translated unit number'>Translated Unit Number</Label>
                                </div>
                            </div>
                        </>
                    }
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="floorOrLevel">Floor or Level:</label></div>
                       {!isFrenchTranslator && <div className='col-md-9'>
                            <Input id="floorOrLevel"
                                type="text"
                                style={{ width: '100%' }}
                                value={addressInput?.floorOrLevel || ''}
                                onChange={changeFloorOrLevel}
                                placeholder='Enter a floor or level (e.g. 5th Flr, etc)'
                                invalid={!!floorError}
                            />
                            <FormFeedback>{floorError}</FormFeedback>
                        </div>
                        }
                        {isFrenchTranslator && <div className='col-md-9'>
                            {addressInput?.floorOrLevel || ''}
                            </div>
                        }
                    </div>
                    {
                        frenchState.isFrenchChecked &&
                        <>
                            <div className='row'>
                                <div className='col-md-3'><label htmlFor="frfloorOrLevel">Floor or Level (French):</label></div>
                                {<div className='col-md-9' lang="fr">{addressInput?.levelNumberFrench || ''}</div>}
                            </div>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <Input id="frfloorOrLevelSync" type="checkbox" checked={addressInput?.levelNumberFrenchSync} onChange={changeFloorOrLevelTranslated} disabled = {!addressInput?.levelNumberFrench} /> {' '}
                                    <Label for="frfloorOrLevelSync" role="region" aria-label='Translated Floor or Level'>Translated Floor or Level</Label>
                                </div>
                            </div>
                        </>
                    }
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="additInfo">Additional Information:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Textarea id="additInfo"
                                ref={addInfoRef}
                                rows={2}
                                maxLength={100}
                                value={addressInput?.additionalInfo || ''}
                                onValueChange={changeAdditionalInfo}
                                placeholder='Enter any additional information (e.g. c/o Macdonald-Carter Bldg Mail Facility)'
                            />
                        </div>
                        }
                        {
                          isFrenchTranslator && <div className='col-md-9'>{addressInput?.additionalInfo || ''}</div>
                        }

                    </div>
                    {
                        frenchState.isFrenchChecked &&
                        <>
                            <div className='row'>
                                <div className='col-md-3'><label htmlFor="fradditInfo">Additional Information (French):</label></div>
                                {<div className='col-md-9' lang="fr">{addressInput?.additionalInfoFrench || ''}</div>}
                            </div>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <Input id="fradditInfoSync" type="checkbox" checked={addressInput?.additionalInfoFrenchSync} onChange={changeAdditionalInfoTranslated} disabled = {!addressInput?.additionalInfoFrench} /> {' '}
                                    <Label for="fradditInfoSync" role="region" aria-label='Translated Additional Information'>Translated Additional Information</Label>
                                </div>
                            </div>
                        </>
                    }
                </CardBody>
            </Card>

            <Card style={{ marginBottom: '1rem' }} >
                <CardHeader>Operational Hours</CardHeader>
                <CardBody>
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="openingTime">Opening Time:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Select id="openingTime" value={addressInput?.openingTimeId || ''} onChange={changeOpeningTime} style={{width:'100%'}}>
                                <option value="" disabled selected hidden>Select an opening time</option>
                                <option value=''></option>
                                {orgValueLists?.orgOpeningTimes?.map((t: IValueListLight) => <option key={t.id} value={t.id}>{t.label}</option>)}

                            </Select>
                        </div>}
                        {isFrenchTranslator && <div className='col-md-9'>
                            {orgValueLists.orgOpeningTimes.map((item: IValueListLight) => {
                                    if (item.id === addressInput?.openingTimeId) {
                                        return item.label;
                                    }
                                    return '';
                                })}
                        </div>

                        }
                    </div>
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="closingTime">Closing Time:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Select id="closingTime" value={addressInput?.closingTimeId || ''} onChange={changeClosingTime} style={{ width: '100%' }}>
                                <option value="" disabled selected hidden>Select a closing time</option>
                                <option value=''></option>
                                {orgValueLists.orgClosingTimes.map((t: IValueListLight) => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </Select>
                        </div>}
                        {isFrenchTranslator && <div className='col-md-9'>
                            {orgValueLists.orgClosingTimes.map((item: IValueListLight) => {
                                if (item.id === addressInput?.closingTimeId) {
                                    return item.label;
                                }
                                return '';
                            })}
                        </div>}
                    </div>
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="oprInfo">Operational Information:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Textarea
                                id="oprInfo"
                                ref={oprInfoRef}
                                rows={3}
                                maxLength={200}
                                value={addressInput?.operationalInfo || ''}
                                onValueChange={changeOperationalInfo}
                                placeholder='Enter any operational information (e.g. closed on third Tuesday every month)'
                            />
                        </div>}
                        {isFrenchTranslator && <div className='col-md-9'>
                            {addressInput?.operationalInfo || ''}
                        </div>
                        }
                    </div>
                    {
                        frenchState.isFrenchChecked &&
                        <>
                            <div className='row'>
                                <div className='col-md-3'><label htmlFor="froprInfo">Operational Information (French):</label></div>
                                {<div className='col-md-9' lang="fr">{addressInput?.operationalInfoFrench || ''}</div>}
                            </div>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <Input id="froppInfoSync" type="checkbox" checked={addressInput?.operationalInfoFrenchSync} onChange={changeOperationalInfoTranslated} disabled = {!addressInput?.operationalInfoFrench} /> {' '}
                                    <Label for="froppInfoSync" role="region" aria-label='Translated Operational Information'>Translated Operational Information</Label>
                                </div>
                            </div>
                        </>
                    }
                    <div className='row'>
                        <div className='col-md-3'><label htmlFor="langProvided">Language Provided:</label></div>
                        {!isFrenchTranslator && <div className='col-md-9'>
                            <Select id="langProvided" value={addressInput?.officeLanguageId || ''} onChange={changeLanguageProvided} style={{ width: '100%' }}>
                                <option value="" disabled selected hidden>Select languages provided</option>
                                <option value=''></option>
                                {orgValueLists.orgOfficeLanguages.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                            </Select>
                        </div>
                        }
                        {isFrenchTranslator && <div className='col-md-9'>
                            {orgValueLists.orgOfficeLanguages.map((item: IValueListLight) => {
                                if (item.id === addressInput?.officeLanguageId) {
                                    return item.label;
                                }
                                return '';
                            })}
                        </div>}
                    </div>
                </CardBody>
            </Card>
        </>
    )
})

export default AddressDetails
